// Project tree on the left (F-18): projects and their shots, file actions, and the card that re-links the videos
// of the shown shot. Rendering is keyed so the per-frame store notifications do not rebuild the DOM.

import type { App } from '../app/app';
import { fsAccess } from '../app/persist';
import type { WsProject, WsShot } from '../app/workspace';
import type { SourceId } from '../core/types';
import { t } from '../i18n';
import { $, esc, message, setText, toast } from './dom';
import { pickSequenceFolder, pickVideos } from './interactions';
import { slotLabel } from '../core/types';

let renaming: { pid: string; sid: string | null } | null = null;
let search = '';
let treeKey = '';
let cardKey = '';
/** The relink card was closed for this shot (it comes back when another shot is shown). */
let cardHiddenFor: string | null = null;

export function initProjectTree(app: App): void {
  const ws = app.ws;
  // New / open / save in one menu left of the search field.
  $('#btnProjMenu').addEventListener('click', (e) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const wp = ws.activeProject();
    showMenu(
      [
        { label: t('tree.newProject'), run: () => newProjectDialog(app) },
        { label: t('toolbar.open'), key: 'Ctrl+O', run: () => app.openProjectDialog() },
        null,
        { label: t('toolbar.save'), key: 'Ctrl+S', disabled: !wp, run: () => app.saveProject(false) },
        { label: t('tree.saveAs'), key: 'Ctrl+Shift+S', disabled: !wp, run: () => app.saveProject(true) },
      ],
      r.left,
      r.bottom + 4,
    );
  });
  $('#btnSide').addEventListener('click', () => app.store.settings((s) => (s.sidebarCollapsed = !s.sidebarCollapsed)));
  initSideSplit(app);
  $('.side-rail').addEventListener('click', () => app.store.settings((s) => (s.sidebarCollapsed = false)));
  $<HTMLInputElement>('#treeSearch').addEventListener('input', (e) => {
    search = (e.target as HTMLInputElement).value.trim().toLowerCase();
    app.store.notify();
  });

  const tree = $('#tree');
  tree.addEventListener('click', (e) => {
    const el = e.target as HTMLElement;
    const add = el.closest<HTMLElement>('[data-add]');
    if (add) {
      void ws.addShot(add.dataset.add!);
      return;
    }
    const node = el.closest<HTMLElement>('.tnode');
    if (!node || el.closest('input')) return;
    if (el.closest('.more')) {
      e.stopPropagation();
      const r = el.closest('.more')!.getBoundingClientRect();
      openMenu(app, node, r.left, r.bottom + 2);
      return;
    }
    const { p, s } = node.dataset;
    if (s) void ws.activate(p!, s);
    else {
      const wp = ws.project(p!);
      if (wp) {
        wp.expanded = !wp.expanded;
        ws.schedulePersist();
        app.store.notify();
      }
    }
  });
  tree.addEventListener('dblclick', (e) => {
    const node = (e.target as HTMLElement).closest<HTMLElement>('.tnode');
    if (!node || (e.target as HTMLElement).closest('.more, input')) return;
    startRename(app, node.dataset.p!, node.dataset.s ?? null);
  });
  tree.addEventListener('contextmenu', (e) => {
    const node = (e.target as HTMLElement).closest<HTMLElement>('.tnode');
    if (!node) return;
    e.preventDefault();
    openMenu(app, node, e.clientX, e.clientY);
  });
  document.addEventListener('pointerdown', (e) => {
    if (!(e.target as HTMLElement).closest('#treeMenu')) $('#treeMenu').hidden = true;
  });
  window.addEventListener('blur', () => ($('#treeMenu').hidden = true));

  // Relink card (F-18.5).
  const card = $('#relinkCard');
  card.addEventListener('click', async (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('button');
    if (!b) return;
    if (b.dataset.act === 'allow') await ws.allowStored();
    else if (b.dataset.act === 'folder' && ws.active) await ws.pickMediaFolder(ws.active.pid);
    else if (b.dataset.act === 'close') {
      cardHiddenFor = ws.active?.sid ?? null;
      app.store.notify();
    } else if (b.dataset.pickdir) await pickSequenceFolder(app, b.dataset.pickdir as SourceId);
    else if (b.dataset.pick) await pickVideos(app, b.dataset.pick as SourceId);
  });
}

// ------------------------------------------------------------------------------------------------ rendering

export function updateProjectTree(app: App): void {
  document.body.classList.toggle('side-collapsed', !!app.state.settings.sidebarCollapsed);
  setText($('#btnSide'), app.state.settings.sidebarCollapsed ? '»' : '«');
  $('#btnSide').title = t(app.state.settings.sidebarCollapsed ? 'side.expand' : 'side.collapse');
  if (!renaming) {
    const html = treeHtml(app);
    if (html !== treeKey) {
      treeKey = html;
      $('#tree').innerHTML = html;
    }
  }
  updateRelinkCard(app);
}

function matches(s: string): boolean {
  return !search || s.toLowerCase().includes(search);
}

function treeHtml(app: App): string {
  const ws = app.ws;
  let html = '';
  for (const wp of ws.projects) {
    const pm = matches(wp.name);
    const shots = wp.shots.filter((s) => pm || matches(ws.shotLabel(wp, s)));
    if (search && !pm && !shots.length) continue;
    const open = wp.expanded || !!search;
    const isActive = ws.active?.pid === wp.id;
    const dirty = ws.isDirty(wp.id) && wp.baseline !== null;
    const sub = wp.fileName
      ? `<span class="tsub"><span class="fname">${esc(wp.fileName)}</span>${wp.readOnly ? ` · ${esc(t('project.readOnly'))}` : ''}</span>`
      : `<span class="tsub unsaved">${esc(t('tree.notSaved'))}</span>`;
    html +=
      `<div class="tnode proj${open ? ' open' : ''}${isActive ? ' cur' : ''}" data-p="${wp.id}" title="${esc(wp.fileName ?? t('tree.notSaved'))}">` +
      `<span class="caret">▶</span><span class="tbody"><span class="tname">${esc(wp.name)}</span>${sub}</span>` +
      (dirty ? `<span class="tdirty" title="${esc(t('tree.dirtyTip'))}">●</span>` : '') +
      `<span class="tcount">${wp.shots.length}</span><button class="more" title="${esc(t('tree.more'))}">⋯</button></div>`;
    if (!open) continue;
    for (const sh of shots) html += shotHtml(app, wp, sh);
    if (!search) html += `<button class="add-shot" data-add="${wp.id}">${esc(t('tree.addShot'))}</button>`;
  }
  return html || `<div class="empty">${esc(t('tree.noMatch'))}</div>`;
}

function shotHtml(app: App, wp: WsProject, sh: WsShot): string {
  const ws = app.ws;
  const p = ws.shotProject(sh);
  const st = ws.shotStatus(sh);
  const hasA = p.sources.some((s) => s.id === 'A');
  const nB = p.sources.filter((s) => s.id !== 'A').length;
  const vids = p.sources.length ? `<span class="pill">${esc(hasA ? t('tree.versions', { n: nB }) : t('tree.videos', { n: p.sources.length }))}</span>` : '';
  // Findings of the automatic quality check kept with the shot (F-10.7).
  const qm = (app as unknown as { quality?: { countFor(p: import('../core/types').Project): number | null } }).quality;
  const qn = qm?.countFor(p) ?? null;
  const qc = qn === null ? '' : `<span class="pill qc-pill${qn ? ' warn' : ''}" title="${esc(t('tree.qcTip'))}">${esc(qn ? t('tree.qcFound', { n: qn }) : t('tree.qcNone'))}</span>`;
  const active = ws.active?.sid === sh.id ? ' active' : '';
  return (
    `<div class="tnode shot${active}" data-p="${wp.id}" data-s="${sh.id}">` +
    `<span class="status-dot st-${st}" title="${esc(t('tree.status.' + st))}"></span>` +
    `<span class="tbody"><span class="tname">${esc(ws.shotLabel(wp, sh))}</span>` +
    (vids || qc ? `<span class="tsub">${vids}${qc}</span>` : '') +
    `</span><button class="more" title="${esc(t('tree.more'))}">⋯</button></div>`
  );
}

function startRename(app: App, pid: string, sid: string | null): void {
  const ws = app.ws;
  const wp = ws.project(pid);
  const sh = sid ? wp?.shots.find((s) => s.id === sid) : null;
  if (!wp || (sid && !sh)) return;
  const node = document.querySelector<HTMLElement>(sid ? `.tnode[data-s="${sid}"]` : `.tnode.proj[data-p="${pid}"]`);
  const nameEl = node?.querySelector('.tname');
  if (!nameEl) return;
  renaming = { pid, sid };
  const input = document.createElement('input');
  input.className = 'rename';
  input.spellcheck = false;
  input.value = sh ? ws.shotProject(sh).name : wp.name;
  nameEl.replaceWith(input);
  input.focus();
  input.select();
  let done = false;
  const finish = (save: boolean) => {
    if (done) return;
    done = true;
    renaming = null;
    treeKey = '';
    const v = input.value.trim();
    if (save && v) {
      if (sid) ws.renameShot(pid, sid, v);
      else ws.renameProject(pid, v);
    }
    app.store.notify();
  };
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') finish(true);
    if (e.key === 'Escape') finish(false);
  });
  input.addEventListener('blur', () => finish(true));
}

// ------------------------------------------------------------------------------------------------ menus

export interface MenuItem {
  label: string;
  run?: () => void | Promise<void>;
  key?: string;
  disabled?: boolean;
  danger?: boolean;
}

function openMenu(app: App, node: HTMLElement, x: number, y: number): void {
  const ws = app.ws;
  const pid = node.dataset.p!;
  const sid = node.dataset.s ?? null;
  const wp = ws.project(pid);
  if (!wp) return;
  const idx = sid ? wp.shots.findIndex((s) => s.id === sid) : ws.projects.indexOf(wp);
  const count = sid ? wp.shots.length : ws.projects.length;
  const items: (MenuItem | null)[] = sid
    ? [
        { label: t('tree.menu.rename'), run: () => startRename(app, pid, sid) },
        { label: t('tree.menu.dupShot'), run: () => ws.duplicateShot(pid, sid) },
        { label: t('tree.menu.moveShot'), run: () => moveShotDialog(app, pid, sid), disabled: !ws.canMoveShot(pid) },
        { label: t('tree.menu.forget'), run: () => ws.forgetVideos(pid, sid) },
        null,
        { label: t('tree.menu.up'), run: () => ws.moveUpDown(pid, sid, -1), disabled: idx <= 0 },
        { label: t('tree.menu.down'), run: () => ws.moveUpDown(pid, sid, 1), disabled: idx >= count - 1 },
        null,
        { label: t('tree.menu.deleteShot'), run: () => deleteShotDialog(app, pid, sid), disabled: count < 2, danger: true },
      ]
    : [
        { label: t('tree.menu.save'), run: () => void ws.save(pid), key: ws.active?.pid === pid ? 'Ctrl+S' : undefined },
        { label: t('tree.menu.saveAs'), run: () => void ws.save(pid, true) },
        { label: t('tree.menu.mediaFolder'), run: () => ws.pickMediaFolder(pid) },
        null,
        { label: t('tree.menu.rename'), run: () => startRename(app, pid, null) },
        { label: t('tree.menu.addShot'), run: () => ws.addShot(pid) },
        { label: t('tree.menu.dupProject'), run: () => ws.duplicateProject(pid) },
        null,
        { label: t('tree.menu.up'), run: () => ws.moveUpDown(pid, null, -1), disabled: idx <= 0 },
        { label: t('tree.menu.down'), run: () => ws.moveUpDown(pid, null, 1), disabled: idx >= count - 1 },
        null,
        { label: t('tree.menu.close'), run: () => closeProjectDialog(app, pid) },
      ];
  showMenu(items, x, y);
}

/** Pop-up menu at a screen position (project tree, panes and source cards); null items are separators. */
export function showMenu(items: (MenuItem | null)[], x: number, y: number): void {
  const menu = $('#treeMenu');
  menu.innerHTML = items
    .map((it, i) =>
      it
        ? `<button data-i="${i}"${it.disabled ? ' disabled' : ''}${it.danger ? ' class="danger"' : ''}>${esc(it.label)}${it.key ? `<span class="k">${esc(it.key)}</span>` : ''}</button>`
        : '<hr />',
    )
    .join('');
  menu.onclick = (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-i]');
    if (!b || b.disabled) return;
    menu.hidden = true;
    void items[Number(b.dataset.i)]?.run?.();
  };
  menu.hidden = false;
  const r = menu.getBoundingClientRect();
  menu.style.left = `${Math.min(x, window.innerWidth - r.width - 6)}px`;
  menu.style.top = `${Math.min(y, window.innerHeight - r.height - 6)}px`;
}

// ------------------------------------------------------------------------------------------------ dialogs

/**
 * New project (F-18.10): its name and a default folder. Downloaded videos are saved there and exports and
 * screenshots start there; without a folder chosen, the folder picker opens when the project is created.
 */
export async function newProjectDialog(app: App): Promise<void> {
  let folder: FileSystemDirectoryHandle | null = null;
  const p = message(
    t('tree.newProjectTitle'),
    `<div class="muted small">${esc(t('tree.projectName'))}</div><input id="msgInput" class="msg-input" spellcheck="false" value="${esc(t('project.untitled'))}" />` +
      `<div class="muted small np-label">${esc(t('tree.folder'))}</div><div class="np-folder"><span id="npFolder" class="np-name muted">${esc(t('tree.folderNone'))}</span><button id="npPick">${esc(t('tree.folderPick'))}</button></div>` +
      `<div class="muted small">${esc(t('tree.folderNote'))}</div>`,
    [{ label: t('tree.create'), primary: true }, { label: t('common.cancel') }],
  );
  const input = $<HTMLInputElement>('#msgInput');
  const pick = async (): Promise<void> => {
    try {
      const dir = await fsAccess.pickDirectory('readwrite');
      if (dir) {
        folder = dir;
        const el = $('#npFolder');
        el.textContent = `📁 ${dir.name}`;
        el.classList.remove('muted');
      }
    } catch (e) {
      if ((e as DOMException).name !== 'AbortError') toast(t('toast.folderFailed', { detail: String(e) }), 5000);
    }
  };
  $('#npPick').addEventListener('click', () => void pick());
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') ($('#msgActions button.primary') as HTMLButtonElement).click();
  });
  setTimeout(() => {
    input.focus();
    input.select();
  }, 0);
  const choice = await p;
  const name = input.value.trim();
  if (choice !== 0 || !name) return;
  // Still inside the click on "Create": the folder picker may open now.
  if (!folder && fsAccess.supported) {
    try {
      folder = await fsAccess.pickDirectory('readwrite');
    } catch {
      folder = null;
    }
  }
  await app.ws.newProject(name, folder ?? undefined);
  if (!folder) toast(t('tree.folderSkipped'), 5000);
}

/** Text prompt on the shared message dialog. Resolves with the trimmed text, or null when cancelled. */
export async function promptText(title: string, label: string, value: string): Promise<string | null> {
  const p = message(title, `<div class="muted small">${esc(label)}</div><input id="msgInput" class="msg-input" spellcheck="false" value="${esc(value)}" />`, [
    { label: t('common.ok'), primary: true },
    { label: t('common.cancel') },
  ]);
  const input = $<HTMLInputElement>('#msgInput');
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') ($('#msgActions button.primary') as HTMLButtonElement).click();
  });
  setTimeout(() => {
    input.focus();
    input.select();
  }, 0);
  const choice = await p;
  const v = input.value.trim();
  return choice === 0 && v ? v : null;
}

async function moveShotDialog(app: App, pid: string, sid: string): Promise<void> {
  const ws = app.ws;
  const others = ws.projects.filter((p) => p.id !== pid);
  const p = message(
    t('tree.moveTitle'),
    `<select id="msgSelect" class="msg-input">${others.map((o) => `<option value="${o.id}">${esc(o.name)}</option>`).join('')}</select>`,
    [{ label: t('common.ok'), primary: true }, { label: t('common.cancel') }],
  );
  const sel = $<HTMLSelectElement>('#msgSelect');
  if ((await p) === 0) ws.moveShot(pid, sid, sel.value);
}

async function deleteShotDialog(app: App, pid: string, sid: string): Promise<void> {
  const ws = app.ws;
  const wp = ws.project(pid);
  const sh = wp?.shots.find((s) => s.id === sid);
  if (!wp || !sh) return;
  const choice = await message(t('tree.deleteTitle'), `<p>${esc(t('tree.deleteBody', { name: ws.shotLabel(wp, sh) }))}</p>`, [
    { label: t('tree.delete'), primary: true },
    { label: t('common.cancel') },
  ]);
  if (choice === 0) await ws.deleteShot(pid, sid);
}

async function closeProjectDialog(app: App, pid: string): Promise<void> {
  const ws = app.ws;
  const wp = ws.project(pid);
  if (!wp) return;
  const unsaved = wp.baseline === null ? wp.shots.some((s) => ws.shotProject(s).sources.length) : ws.isDirty(pid);
  if (unsaved) {
    const choice = await message(t('tree.closeTitle'), `<p>${esc(t('tree.closeBody', { name: wp.name }))}</p>`, [
      { label: t('tree.closeSave'), primary: true },
      { label: t('tree.closeDiscard') },
      { label: t('common.cancel') },
    ]);
    if (choice === 0 && !(await ws.save(pid))) return;
    if (choice !== 0 && choice !== 1) return;
  }
  await ws.closeProject(pid);
}

// ------------------------------------------------------------------------------------------------ relink card

function fmtSize(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1e3))} KB`;
}

function updateRelinkCard(app: App): void {
  const ws = app.ws;
  const card = $('#relinkCard');
  const sh = ws.activeShot();
  const wp = ws.activeProject();
  const p = app.project;
  const s = app.sources;
  const missing = p.sources.filter((x) => !s.runtime.has(x.id));
  const show = !!sh && !!wp && (missing.length > 0 || ws.scanning) && cardHiddenFor !== sh.id;
  if (sh && cardHiddenFor && cardHiddenFor !== sh.id) cardHiddenFor = null;
  const key = show
    ? JSON.stringify([sh!.id, ws.shotLabel(wp!, sh!), ws.scanning, ws.hasStoredHandles(), p.sources.map((x) => [x.id, x.file.name, s.runtime.has(x.id), s.loading.has(x.id)]), t('tree.title')])
    : '';
  if (key === cardKey) return;
  cardKey = key;
  card.hidden = !show;
  if (!show) return;
  const slots = p.sources
    .map((x) => {
      const ok = s.runtime.has(x.id);
      const loading = s.loading.has(x.id);
      const spec = `${fmtSize(x.file.size)} · ${x.spec.width}×${x.spec.height} · ${Math.round(x.spec.frameRate * 1000) / 1000} fps`;
      const right = ok
        ? `<span class="st">${esc(t('relink.linked'))}</span>`
        : loading
          ? `<span class="st">${esc(t('relink.loading'))}</span>`
          : x.sequence
            ? `<button data-pickdir="${x.id}">${esc(t('drop.pickSeq'))}</button>`
            : `<button data-pick="${x.id}">${esc(t('relink.pick'))}</button>`;
      return `<div class="rslot${ok ? ' ok' : ''}"><span class="id">${slotLabel(x.id)}</span><span class="fn"><div class="n" title="${esc(x.file.name)}">${esc(x.file.name)}</div><div class="s">${esc(spec)}</div></span>${right}</div>`;
    })
    .join('');
  card.innerHTML =
    `<div style="display:flex;align-items:center;gap:8px"><h3>${esc(t('relink.title', { name: ws.shotLabel(wp!, sh!) }))}</h3><span class="spacer"></span>` +
    `<button class="ghost" data-act="close" title="${esc(t('relink.hide'))}">✕</button></div>` +
    `<div class="desc">${esc(t('relink.desc'))}</div>` +
    `<div class="acts">` +
    (ws.hasStoredHandles() ? `<button class="primary" data-act="allow" title="${esc(t('relink.allowTip'))}">${esc(t('relink.allow'))}</button>` : '') +
    `<button data-act="folder" title="${esc(t('relink.folderTip'))}"${ws.scanning ? ' disabled' : ''}>${esc(ws.scanning ? t('relink.scanning') : t('relink.folder'))}</button></div>` +
    slots;
}

/** Divider between the project tree and the tool panel: dragging sets the tree height, kept in the settings. */
function initSideSplit(app: App): void {
  const split = $('#sideSplit');
  const tree = $('#tree');
  const apply = () => {
    const h = app.state.settings.sideTreeHeight;
    tree.style.height = h ? `${h}px` : '';
    tree.style.maxHeight = h ? 'none' : '';
  };
  split.addEventListener('pointerdown', (e) => {
    split.setPointerCapture(e.pointerId);
    split.classList.add('drag');
    const top = tree.getBoundingClientRect().top;
    // The tool panel keeps at least 180 px; the tree at least 60 px.
    const max = $('#side').getBoundingClientRect().bottom - top - 180 - (split.getBoundingClientRect().top - tree.getBoundingClientRect().bottom);
    const move = (ev: PointerEvent) => {
      const h = Math.round(Math.max(60, Math.min(max, ev.clientY - top)));
      app.state.settings.sideTreeHeight = h;
      apply();
    };
    const up = () => {
      split.classList.remove('drag');
      split.removeEventListener('pointermove', move);
      split.removeEventListener('pointerup', up);
      app.store.settings(() => undefined);
    };
    split.addEventListener('pointermove', move);
    split.addEventListener('pointerup', up);
  });
  // Double-click: back to the automatic height.
  split.addEventListener('dblclick', () => {
    app.store.settings((st) => (st.sideTreeHeight = undefined));
    apply();
  });
  app.store.subscribe(apply);
  apply();
}
